import { environment } from '../../environments/environment';
import { PortalType } from '../models/enum/shared/portal-type';
import { EmployerUser } from '../models/users/dto/employer-user';

export class Endpoints {
  static encodeParam(s: string): string {
    return encodeURIComponent(s);
  }

  static getBasePath(type: PortalType): string {
    switch (type) {
      case PortalType.Member:
        return 'members';
      case PortalType.Internal:
        return 'internalusers';
      case PortalType.Employer:
        return 'employers';
      default:
        throw new Error(`Invalid user type: ${type}`);
    }
  }

  static generateUrl(email: string, type: PortalType, pathSuffix: string): string {
    const basePath = this.getBasePath(type);
    return `${environment.mainServiceUrl}/${basePath}/${email}/${pathSuffix}`;
  }

  // Session

  static signIn(portalType: PortalType): string {
    return environment.mainServiceUrl + `/${this.getBasePath(portalType)}/login`;
  }

  static signOut(email: string, type: PortalType): string {
    return this.generateUrl(email, type, 'logout');
  }

  static refreshSession(email: string, portalType: PortalType): string {
    return this.generateUrl(email, portalType, 'refreshsession');
  }

  static changeEmail(email: string, type: PortalType): string {
    return this.generateUrl(email, type, 'changeemail');
  }

  static verifyEmail(email: string, type: PortalType): string {
    return this.generateUrl(email, type, 'verifyemail');
  }

  static changePassword(email: string, type: PortalType): string {
    return this.generateUrl(email, type, 'changepassword');
  }

  static changeMfaPhone(email: string, type: PortalType): string {
    return this.generateUrl(email, type, 'changemfaphonenumber');
  }

  static verifySmsCode(email: string, type: PortalType): string {
    return this.generateUrl(email, type, 'verifymfaphonenumber');
  }

  static verifyMfaToken(email: string, type: PortalType): string {
    return this.generateUrl(email, type, 'verifymfatokensetup');
  }

  static disableMfa(email: string, type: PortalType): string {
    return this.generateUrl(email, type, 'disablemfa');
  }

  static getVerifiedPhoneNumber(email: string, type: PortalType): string {
    return this.generateUrl(email, type, 'verifiedphonenumber');
  }

  static associateMfaToken(email: string, type: PortalType): string {
    return this.generateUrl(email, type, 'associatemfatoken');
  }

  // Member Auth

  static verifyMember(memberInviteId: string): string {
    return environment.mainServiceUrl + `/invites/${memberInviteId}/verify`;
  }

  static confirmMember(memberInviteId: string): string {
    return environment.mainServiceUrl + `/invites/${memberInviteId}/complete`;
  }

  static verifyMemberEmail(): string {
    return environment.mainServiceUrl + `/members/confirmmember`;
  }

  // Forgot Password

  static generateForgotPasswordUrl(email: string, type: PortalType): string {
    return this.generateUrl(email, type, 'forgotpassword');
  }

  static generateResetPasswordUrl(email: string, type: PortalType): string {
    return this.generateUrl(email, type, 'resetforgottenpassword');
  }

  // Eula

  static getEula(portalTypeId: number): string {
    return environment.mainServiceUrl + `/portaltypes/${portalTypeId}/enduserlicenceagreements`;
  }

  // Members

  static ipCreateMember(): string {
    return environment.mainServiceUrl + `/internal/member`;
  }

  static ipCreateNonMember(): string {
    return environment.mainServiceUrl + `/internal/nonmember`;
  }

  static updateMemberOnboardingInformation(): string {
    return environment.mainServiceUrl + `/member/onboardinginformation`;
  }

  // Each Portal has its own distinct Get Member By ID route

  static mpGetMember(): string {
    return environment.mainServiceUrl + `/odata/member/members`;
  }

  static internalPortalGetMemberById(memberId: number): string {
    return environment.mainServiceUrl + `/odata/internal/members/${memberId}`;
  }

  static ipRestrictMemberPortalAccess(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/restrictportalaccess`;
  }

  static ipRestoreMemberPortalAccess(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/restoreportalaccess`;
  }

  static epGetMemberById(memberId: number): string {
    return environment.mainServiceUrl + `/odata/employer/members/${memberId}`;
  }

  static ipGetMemberRetirementStub(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/retirementstubs`;
  }

  static ipUpdateMemberRetirementStub(memberId: number, retirementStubId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/retirementstubs/${retirementStubId}`;
  }

  static mpGetMemberRetirementType(): string {
    return environment.mainServiceUrl + `/member/retirementtype`;
  }

  static ipGetMemberLifeStatus(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/lifestatus`;
  }

  static ipUpdateMemberLifeStatus(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/lifestatus`;
  }

  static ipGetMemberPaymentSuspension(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/paymentsuspension`;
  }

  static ipSuspendMemberPayments(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/paymentsuspension/suspend`;
  }

  static ipResumeMemberPayments(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/paymentsuspension/resume`;
  }

  static ipGetMemberDeathRecord(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/deathrecords`;
  }

  static ipCreateMemberDeathRecord(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/deathrecords`;
  }

  static ipUpdateDeathRecordAdminDetails(memberId: number, deathRecordId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/deathrecords/${deathRecordId}/admindetails`;
  }

  static ipCloseDeathRecord(memberId: number, deathRecordId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/deathrecords/${deathRecordId}/close`;
  }

  static ipReopenDeathRecord(memberId: number, deathRecordId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/deathrecords/${deathRecordId}/reopen`;
  }

  static ipGetMemberGarnishmentOrders(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/garnishmentorders`;
  }

  static ipCreateGarnishmentOrder(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/garnishmentorders`;
  }

  static ipCancelGarnishmentOrder(memberId: number, orderId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/garnishmentorders/${orderId}/cancel`;
  }

  static ipArchiveDeathRecord(memberId: number, deathRecordId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/deathrecords/${deathRecordId}/archive`;
  }

  static ipCreateDeathRecordReminder(memberId: number, deathRecordId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/deathrecords/${deathRecordId}/reminders`;
  }

  static ipDeleteDeathRecordReminder(memberId: number, deathRecordId: number, reminderId: number): string {
    return (
      environment.mainServiceUrl + `/internal/members/${memberId}/deathrecords/${deathRecordId}/reminders/${reminderId}`
    );
  }

  static ipDeathRecordNotifyingParty(memberId: number, deathRecordId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/deathrecords/${deathRecordId}/notifyingparty`;
  }

  static ipDeathRecordLegalRepresentative(memberId: number, deathRecordId: number): string {
    return (
      environment.mainServiceUrl + `/internal/members/${memberId}/deathrecords/${deathRecordId}/legalrepresentative`
    );
  }

  static ipAddDeathRecordSettlement(memberId: number, deathRecordId: number, deathRecordAccountId: number): string {
    return (
      environment.mainServiceUrl +
      `/internal/members/${memberId}/deathrecords/${deathRecordId}/accounts/${deathRecordAccountId}/settlements`
    );
  }

  static ipDeathRecordSettlement(memberId: number, deathRecordId: number, settlementId: number): string {
    return (
      environment.mainServiceUrl +
      `/internal/members/${memberId}/deathrecords/${deathRecordId}/settlements/${settlementId}`
    );
  }

  static ipReleaseDeathRecordSettlement(memberId: number, deathRecordId: number, settlementId: number): string {
    return Endpoints.ipDeathRecordSettlement(memberId, deathRecordId, settlementId) + '/release';
  }

  static ipCreateDeathRecordAttachment(memberId: number, deathRecordId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/deathrecords/${deathRecordId}/attachments`;
  }

  static ipDeleteDeathRecordAttachment(memberId: number, deathRecordId: number, attachmentId: string): string {
    return Endpoints.ipCreateDeathRecordAttachment(memberId, deathRecordId) + `/${attachmentId}`;
  }

  static ipGetDeathRecordComments(deathRecordId: number): string {
    return environment.mainServiceUrl + `/odata/internal/deathrecords/${deathRecordId}/comments`;
  }

  static ipAddDeathRecordComment(deathRecordId: number): string {
    return environment.mainServiceUrl + `/internal/deathrecords/${deathRecordId}/comments`;
  }

  static ipCreateDeathRecordCommentReadReceipts(): string {
    return environment.mainServiceUrl + `/internal/deathrecords/comments/readreceipts`;
  }

  static getAllInternalMembers(): string {
    return environment.mainServiceUrl + `/odata/internal/members`;
  }

  static getMemberSIN(): string {
    return environment.mainServiceUrl + `/odata/member/socialinsurancenumber`;
  }

  static memberForceChangeEmail(memberId: number): string {
    return environment.mainServiceUrl + `/members/${memberId}/forcechangeemail`;
  }

  static memberComments(memberId: string): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/comments`;
  }

  static ipGetMemberRecentSubmissions(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/recentsubmissions`;
  }

  static ipGetEmployerRecentSubmissions(employerId: number): string {
    return environment.mainServiceUrl + `/internal/employers/${employerId}/recentsubmissions`;
  }

  static ipCreateMemberRecordReadReceipts(): string {
    return environment.mainServiceUrl + `/internal/members/comments/readreceipts`;
  }

  static memberForceChangePassword(memberId: string): string {
    return environment.mainServiceUrl + `/members/${memberId}/forceresetpassword`;
  }

  static searchInternalMembersByIdOrName(): string {
    return environment.mainServiceUrl + `/odata/internal/membersearchbyidorname`;
  }

  static epSearchMembersByIdOrName(): string {
    return environment.mainServiceUrl + `/odata/employer/membersearchbyidorname`;
  }

  static epGetEmployerMembers(): string {
    return environment.mainServiceUrl + `/odata/employer/members`;
  }

  static resendMemberInvite(memberId: string): string {
    return environment.mainServiceUrl + `/members/${memberId}/resendinvite`;
  }

  static getCommentsForMember(memberId: string): string {
    return environment.mainServiceUrl + `/odata/internal/members/${memberId}/comments`;
  }

  static mpUpdateMemberSpouseContactInfo(spouseId: number): string {
    return environment.mainServiceUrl + `/member/spouses/${spouseId}/contactinformation`;
  }

  static addHistoricalRelationshipStatus(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/historicalrelationshipstatus`;
  }

  static ipUpdateMemberSpouseContactInfo(memberId: number, spouseId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/spouses/${spouseId}/contactinformation`;
  }

  static ipGetNewMembersToVerify(): string {
    return environment.mainServiceUrl + `/internal/newmemberverification`;
  }

  static ipCreateMembers(): string {
    return environment.mainServiceUrl + `/internal/members`;
  }

  // Types

  static getStatesByCountryId(countryId: number): string {
    return environment.mainServiceUrl + `/countrytypes/${countryId}/statetypes`;
  }

  static getGenders(): string {
    return environment.mainServiceUrl + `/gendertypes`;
  }

  static getRetirementTypes(): string {
    return environment.mainServiceUrl + `/retirementtypes`;
  }

  static getLifeStatusTypes(): string {
    return environment.mainServiceUrl + `/lifestatustypes`;
  }

  static getPronouns(): string {
    return environment.mainServiceUrl + `/pronountypes`;
  }

  static getCountries(): string {
    return environment.mainServiceUrl + `/countrytypes`;
  }

  static getCountriesWithStates(): string {
    return environment.mainServiceUrl + `/countrieswithstates`;
  }

  static getSubmissionStatusTypes(): string {
    return environment.mainServiceUrl + `/submissionstatustypes`;
  }

  static getAlertTypes(): string {
    return environment.mainServiceUrl + `/alertTypes`;
  }

  static getTransactionEntryCodeTypes(): string {
    return environment.mainServiceUrl + `/transactionentrycodetypes`;
  }

  static getContributionEntryOptionTypes(): string {
    return environment.mainServiceUrl + `/transactionentrycodetypes?categoryId=1`;
  }

  static getTransactionStatusTypes(): string {
    return environment.mainServiceUrl + `/transactionstatustypes`;
  }

  static getMfaTypes(): string {
    return environment.mainServiceUrl + `/multifactorauthenticationtypes`;
  }

  static getEmployerPlanDesignatedUnitTypes(): string {
    return environment.mainServiceUrl + `/employerplandesignatedunittypes`;
  }

  static getEmployerPlanContributionRateBasisTypes(): string {
    return environment.mainServiceUrl + `/employerplancontributionratebasistypes`;
  }

  static getEmployerPlanContributionRateCalculationTypes(): string {
    return environment.mainServiceUrl + `/employerplanmatchedcontributionratecalculationtypes`;
  }

  static getEmployerPlanLessThanFullTimeParticipationTypes(): string {
    return environment.mainServiceUrl + `/employerplanlessthanfulltimeparticipationtypes`;
  }

  static getRemittanceFrequencyTypes(): string {
    return environment.mainServiceUrl + `/employerplanremittancefrequencytypes`;
  }

  static getEmployerPlanTypes(): string {
    return environment.mainServiceUrl + `/employerplantypes`;
  }

  static getMemberEmploymentTypes(): string {
    return environment.mainServiceUrl + `/memberemploymenttypes`;
  }

  static getMemberEmploymentLeaveTypes(): string {
    return environment.mainServiceUrl + `/memberemploymentleavetypes`;
  }

  static getMemberEmploymentStatusTypes(): string {
    return environment.mainServiceUrl + `/memberemploymentstatustypes`;
  }

  static getMemberEmploymentStatusChangeReasonTypes(): string {
    return environment.mainServiceUrl + `/memberemploymentstatuschangereasontypes`;
  }

  static getMemberEmploymentStatusChangeDefinitionTypes(): string {
    return environment.mainServiceUrl + `/memberemploymentstatuschangedefinitiontypes`;
  }

  static getMemberRelationshipStatusTypes(): string {
    return environment.mainServiceUrl + `/memberrelationshipstatustypes`;
  }

  static getFundTypes(): string {
    return environment.mainServiceUrl + `/fundtypes`;
  }

  static getPaylineCurrencyTypes(): string {
    return environment.mainServiceUrl + `/paylinecurrencytypes`;
  }

  static getPaymentMethodTypes(): string {
    return environment.mainServiceUrl + `/paymentmethodtypes`;
  }

  static getBeneficiaryRelationshipToMemberTypes(): string {
    return environment.mainServiceUrl + `/beneficiaryrelationshiptomembertypes`;
  }

  static getBeneficiarySpousalDefinitionTypes(): string {
    return environment.mainServiceUrl + `/spousaldefinitiontypes`;
  }

  static getTaxFormTypes(): string {
    return environment.mainServiceUrl + `/taxformtypes`;
  }

  static getTaxResidencyTypes(): string {
    return environment.mainServiceUrl + `/taxresidencytypes`;
  }

  static getTaxStatusTypes(): string {
    return environment.mainServiceUrl + `/taxstatustypes`;
  }

  static getTaxProfileSourceTypes(): string {
    return environment.mainServiceUrl + `/taxprofilesourcetypes`;
  }

  static getPrescribedTaxZoneTypes(): string {
    return environment.mainServiceUrl + `/prescribedtaxzonetypes`;
  }

  static getTaxWithholdingOverrideTypes(): string {
    return environment.mainServiceUrl + `/taxwithholdingoverridetypes`;
  }

  static getTaxCraLetterOfAuthorityTypes(): string {
    return environment.mainServiceUrl + `/taxcraletterofauthoritytypes`;
  }

  // Internal Users

  static getInternalUsers(): string {
    return environment.mainServiceUrl + `/odata/internal/internalusers`;
  }

  static getInternalUsersById(id: number): string {
    return environment.mainServiceUrl + `/odata/internal/internalusers/${id}`;
  }

  static updateInternalUser(internalUserId: string): string {
    return environment.mainServiceUrl + `/internal/internalusers/${internalUserId}`;
  }

  static forceResetInternalUserPassword(internalUserId: string): string {
    return environment.mainServiceUrl + `/internal/internalusers/${internalUserId}/forceresetpassword`;
  }

  static forceResetInternalUserEmail(internalUserId: string): string {
    return environment.mainServiceUrl + `/internal/internalusers/${internalUserId}/forcechangeemail`;
  }

  static createInternalUser(): string {
    return environment.mainServiceUrl + `/internal/internalusers`;
  }

  // Roles and Permissions
  static getInternalUserRoles(userId: string): string {
    return environment.mainServiceUrl + `/odata/internal/internalusers/${userId}/roles`;
  }

  static getInternalUserSubmissionPermissions(userId: number): string {
    return environment.mainServiceUrl + `/internal/internalusers/${userId}/submissionpermissions`;
  }

  static getEmployerUserRoles(): string {
    return environment.mainServiceUrl + `/odata/employer/tokenholderroles`;
  }

  static getInternalRoles(): string {
    return environment.mainServiceUrl + `/odata/internal/internalroles`;
  }

  static ipGetEmployerRoles(): string {
    return environment.mainServiceUrl + `/odata/internal/employerroles`;
  }

  static epGetEmployerRoles(): string {
    return environment.mainServiceUrl + `/odata/employer/roles`;
  }

  // Employers

  static ipGetEmployerById(employerId: number): string {
    return environment.mainServiceUrl + `/odata/internal/employers/${employerId}`;
  }

  static epGetEmployer(): string {
    return environment.mainServiceUrl + `/odata/employer/employers`;
  }

  static ipGetEmployers(): string {
    return environment.mainServiceUrl + `/odata/internal/employers`;
  }

  static epGetEmployerLogo(): string {
    return environment.mainServiceUrl + `/employer/logo`;
  }

  static mpGetEmployerLogo(employerId: number): string {
    return environment.mainServiceUrl + `/member/employers/${employerId}/logo`;
  }

  static ipGetEmployerUsers(employerId: number): string {
    return environment.mainServiceUrl + `/odata/internal/employers/${employerId}/users`;
  }

  static ipGetEmployerMembers(employerId: number): string {
    return environment.mainServiceUrl + `/odata/internal/employers/${employerId}/members`;
  }

  static getEmployerUsers(): string {
    return environment.mainServiceUrl + `/odata/employer/users`;
  }

  static ipCreateEmployerUser(employerId: number): string {
    return environment.mainServiceUrl + `/internal/employers/${employerId}/users`;
  }

  static createEmployerUser(): string {
    return environment.mainServiceUrl + `/employer/users`;
  }

  static ipUpdateEmployer(employerId: number): string {
    return environment.mainServiceUrl + `/internal/employers/${employerId}`;
  }

  static ipGetEmployerUser(employerId: number, userId: number): string {
    return environment.mainServiceUrl + `/odata/internal/employers/${employerId}/users/${userId}`;
  }

  static epGetEmployerUser(userId: number): string {
    return environment.mainServiceUrl + `/odata/employer/users/${userId}`;
  }

  static ipUpdateEmployerUser(employerId: number, userId: number): string {
    return environment.mainServiceUrl + `/internal/employers/${employerId}/users/${userId}`;
  }

  static updateEmployerUser(userId: number): string {
    return environment.mainServiceUrl + `/employer/users/${userId}`;
  }

  static employerForceChangeEmail(u: EmployerUser): string {
    return environment.mainServiceUrl + `/employers/${u?.employerId}/users/${u?.id}/forcechangeemail`;
  }

  static employerForceChangePassword(u: EmployerUser): string {
    return environment.mainServiceUrl + `/employers/${u?.employerId}/users/${u?.id}/forceresetpassword`;
  }

  static searchEmployersByIdOrName(): string {
    return environment.mainServiceUrl + `/odata/internal/employersearchbyidorname`;
  }

  static getInternalMemberEmploymentHistory(memberId: number): string {
    return environment.mainServiceUrl + `/odata/internal/members/${memberId}/employmenthistory`;
  }

  static getEmployerMemberEmploymentHistory(memberId: number): string {
    return environment.mainServiceUrl + `/odata/employer/members/${memberId}/employmenthistory`;
  }

  static getMemberEmploymentHistory(): string {
    return environment.mainServiceUrl + `/odata/member/employmenthistory`;
  }

  static createInternalMemberEmploymentHistory(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/employmenthistory`;
  }

  static updateInternalMemberEmploymentHistory(memberId: number, employmentHistoryId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/employmenthistory/${employmentHistoryId}`;
  }

  static updateEmployerMemberEmploymentHistory(memberId: number, employmentHistoryId: number): string {
    return environment.mainServiceUrl + `/employer/members/${memberId}/employmenthistory/${employmentHistoryId}`;
  }

  // Employer Plans

  static getInternalEmployerPlan(employerPlanId: string): string {
    return environment.mainServiceUrl + `/odata/internal/employerplans/${employerPlanId}`;
  }

  static getEmployerEmployerPlan(employerPlanId: string): string {
    return environment.mainServiceUrl + `/odata/employer/plans/${employerPlanId}`;
  }

  static updateEmployerPlan(employerPlanId: string): string {
    return environment.mainServiceUrl + `/internal/employerplans/${employerPlanId}`;
  }

  static ipGetEmployerPlanDesignatedUnits(employerPlanId: number): string {
    return environment.mainServiceUrl + `/odata/internal/employerplans/${employerPlanId}/designatedunits`;
  }

  static epGetEmployerPlanDesignatedUnits(employerPlanId: number): string {
    return environment.mainServiceUrl + `/odata/employer/plans/${employerPlanId}/designatedunits`;
  }

  static epGetEmployerPlanDesignatedUnitById(employerPlanId: number, designatedUnitId: number): string {
    return environment.mainServiceUrl + `/odata/employer/plans/${employerPlanId}/designatedunits/${designatedUnitId}`;
  }

  static ipCreateEmployerPlanDesignatedUnit(employerPlanId: number): string {
    return environment.mainServiceUrl + `/internal/employerplans/${employerPlanId}/designatedunits`;
  }

  static getEmployerEmployerPlanDesignatedUnits(employerPlanId: string): string {
    return environment.mainServiceUrl + `/odata/employer/plans/${employerPlanId}/designatedunits`;
  }

  static ipCreateEmployerPlanDesignatedUnitVersion(employerPlanId: number, unitId: number): string {
    return environment.mainServiceUrl + `/internal/employerplans/${employerPlanId}/designatedunits/${unitId}/versions`;
  }

  static ipUpdateEmployerPlanDesignatedUnitVersion(employerPlanId: number, unitId: number, versionId: string): string {
    return (
      environment.mainServiceUrl +
      `/internal/employerplans/${employerPlanId}/designatedunits/${unitId}/versions/${versionId}`
    );
  }

  static addEmployerPlanComment(planId: string): string {
    return environment.mainServiceUrl + `/internal/employerplans/${planId}/comments`;
  }

  static createEmployerPlanDesignatedUnit(planId: string): string {
    return environment.mainServiceUrl + `/internal/employerplans/${planId}/designatedunits`;
  }

  static ipUpdateEmployerPlanDesignatedUnit(planId: number, unitId: number): string {
    return environment.mainServiceUrl + `/internal/employerplans/${planId}/designatedunits/${unitId}`;
  }

  static searchInternalActiveDesignatedUnits(planId: number) {
    return environment.mainServiceUrl + `/odata/internal/employerplans/${planId}/activedesignatedunits`;
  }

  static searchEmployerActiveDesignatedUnits(planId: number) {
    return environment.mainServiceUrl + `/odata/employer/plans/${planId}/activedesignatedunits`;
  }

  // Submissions

  static ipGetMemberSubmissions(): string {
    return environment.mainServiceUrl + `/odata/internal/membersubmissions`;
  }

  static ipGetEmployerSubmissions(): string {
    return environment.mainServiceUrl + `/odata/internal/employersubmissions`;
  }

  static epGetEmployerSubmissions(): string {
    return environment.mainServiceUrl + `/odata/employer/submissions`;
  }

  static epGetEmployerSubmissionById(submissionId: string): string {
    return environment.mainServiceUrl + `/odata/employer/submissions/${submissionId}`;
  }

  static epUpdateEmployerAddress(): string {
    return environment.mainServiceUrl + `/employer/submissions/updateaddress`;
  }

  static ipGetSharedSubmissions(): string {
    return environment.mainServiceUrl + `/odata/internal/sharedsubmissions`;
  }

  static ipGetMemberSubmissionById(submissionId: string): string {
    return environment.mainServiceUrl + `/odata/internal/membersubmissions/${submissionId}`;
  }

  static ipGetEmployerSubmissionById(submissionId: string): string {
    return environment.mainServiceUrl + `/odata/internal/employersubmissions/${submissionId}`;
  }

  static mpGetSubmissionById(submissionId: string): string {
    return environment.mainServiceUrl + `/odata/member/submissions/${submissionId}`;
  }

  static mpGetMemberSubmissions(): string {
    return environment.mainServiceUrl + `/odata/member/submissions`;
  }

  static ipGetMemberSubmissionViewersForMemberSubmissionType(): string {
    return environment.mainServiceUrl + `/odata/internal/membersubmissionviewers`;
  }

  static ipGetEmployerSubmissionViewersForEmployerSubmissionType(): string {
    return environment.mainServiceUrl + `/odata/internal/employersubmissionviewers`;
  }

  static ipGetMemberCommentMentionables(): string {
    return environment.mainServiceUrl + `/odata/internal/membercommentmentionableusers`;
  }

  static ipGetEmployerCommentMentionables(): string {
    return environment.mainServiceUrl + `/odata/internal/employercommentmentionableusers`;
  }

  static ipGetCommentsForEmployer(employerId: string): string {
    return environment.mainServiceUrl + `/odata/internal/employers/${employerId}/comments`;
  }

  static ipAddEmployerRecordComment(employerId: string): string {
    return environment.mainServiceUrl + `/internal/employers/${employerId}/comments`;
  }

  static ipCreateEmployerCommentReadReceipts(): string {
    return environment.mainServiceUrl + `/internal/employers/comments/readreceipts`;
  }

  static createInternalNameChangeSubmission(): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/namechange`;
  }

  static createNameChangeSubmission(): string {
    return environment.mainServiceUrl + `/member/submissions/namechange`;
  }

  static createInternalAddressChangeSubmission(): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/addresschange`;
  }

  static createAddressChangeSubmission(): string {
    return environment.mainServiceUrl + `/member/submissions/addresschange`;
  }

  static createInternalPersonalInfoSubmission(): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/personalinformation`;
  }

  static createPersonalInfoSubmission(): string {
    return environment.mainServiceUrl + `/member/submissions/personalinformation`;
  }

  static createInternalUpdateContactInformationSubmission(): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/contactinformation`;
  }

  static createUpdateContactInformationSubmission(): string {
    return environment.mainServiceUrl + `/member/submissions/contactinformation`;
  }

  static ipCreateMemberSubmissionAttachment(submissionId: string): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/${submissionId}/attachments`;
  }

  static ipUpdateBeneficiarySpousalWaiver(submissionId: string, spousalWaiverId: string): string {
    return (
      environment.mainServiceUrl +
      `/internal/membersubmissions/${submissionId}/beneficiaryspousalwaivers/${spousalWaiverId}`
    );
  }

  static ipCreateEmployerSubmissionAttachment(submissionId: string): string {
    return environment.mainServiceUrl + `/internal/employersubmissions/${submissionId}/attachments`;
  }

  static createInternalMemberInvestmentInstructionsSubmission(): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/investmentinstructions`;
  }

  static createMemberInvestmentInstructionsSubmission(): string {
    return environment.mainServiceUrl + `/member/submissions/investmentinstructions`;
  }

  static mpCreateRelationshipStatusSubmission(): string {
    return environment.mainServiceUrl + `/member/submissions/relationshipstatus`;
  }

  static ipCreateRelationshipStatusSubmission(): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/relationshipstatus`;
  }

  static mpCreateVBBeneficiaryUpdateSubmission(): string {
    return environment.mainServiceUrl + `/member/submissions/updatevariablebenefitbeneficiaries`;
  }

  static mpCreateDCBeneficiaryUpdateSubmission(): string {
    return environment.mainServiceUrl + `/member/submissions/updatedefinedcontributionbeneficiaries`;
  }

  static mpCreateMonthlyPensionBeneficiaryUpdateSubmission(): string {
    return environment.mainServiceUrl + `/member/submissions/updatemonthlypensionbeneficiaries`;
  }

  static ipCreateVBBeneficiaryUpdateSubmission(): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/updatevariablebenefitbeneficiaries`;
  }

  static ipCreateDCBeneficiaryUpdateSubmission(): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/updatedefinedcontributionbeneficiaries`;
  }

  static ipCreateMonthlyPensionBeneficiaryUpdateSubmission(): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/updatemonthlypensionbeneficiaries`;
  }

  static ipAddMemberSubmissionComment(submissionId: string): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/${submissionId}/comments`;
  }

  static ipAddEmployerSubmissionComment(submissionId: string): string {
    return environment.mainServiceUrl + `/internal/employersubmissions/${submissionId}/comments`;
  }

  static ipSetMemberSubmissionCommentsAsRead(): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/comments/readreceipts`;
  }

  static ipSetEmployerSubmissionCommentsAsRead(): string {
    return environment.mainServiceUrl + `/internal/employersubmissions/comments/readreceipts`;
  }

  static ipDeleteMemberSubmissionAttachment(attachmentId: string, submissionId: string): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/${submissionId}/attachments/${attachmentId}`;
  }

  static ipDeleteEmployerSubmissionAttachment(attachmentId: string, submissionId: string): string {
    return environment.mainServiceUrl + `/internal/employersubmissions/${submissionId}/attachments/${attachmentId}`;
  }

  static ipGetMemberSubmissionAttachments(submissionId: string): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/${submissionId}/attachments`;
  }

  static ipGetEmployerSubmissionAttachments(submissionId: string): string {
    return environment.mainServiceUrl + `/internal/employersubmissions/${submissionId}/attachments`;
  }

  static ipUserCancelMemberSubmission(submissionId: number) {
    return environment.mainServiceUrl + `/internal/membersubmissions/${submissionId}/cancel`;
  }

  static ipUserCancelEmployerSubmission(submissionId: string) {
    return environment.mainServiceUrl + `/internal/employersubmissions/${submissionId}/cancel`;
  }

  static ipSetMemberSubmissionToMemberActionRequired(submissionId: string) {
    return environment.mainServiceUrl + `/internal/membersubmissions/${submissionId}/memberactionrequired`;
  }

  static ipSetEmployerSubmissionToEmployerActionRequired(submissionId: string) {
    return environment.mainServiceUrl + `/internal/employersubmissions/${submissionId}/employeractionrequired`;
  }

  static ipSetMemberSubmissionToRejected(submissionId: number) {
    return environment.mainServiceUrl + `/internal/membersubmissions/${submissionId}/rejected`;
  }

  static ipSetEmployerSubmissionToRejected(submissionId: string) {
    return environment.mainServiceUrl + `/internal/employersubmissions/${submissionId}/rejected`;
  }

  static ipSetMemberSubmissionToProcessing(submissionId: string) {
    return environment.mainServiceUrl + `/internal/membersubmissions/${submissionId}/processing`;
  }

  static ipSetEmployerSubmissionToProcessing(submissionId: string) {
    return environment.mainServiceUrl + `/internal/employersubmissions/${submissionId}/processing`;
  }

  static ipSetMemberSubmissionToApproved(submissionId: string) {
    return environment.mainServiceUrl + `/internal/membersubmissions/${submissionId}/approved`;
  }

  static ipSetEmployerSubmissionToApproved(submissionId: string) {
    return environment.mainServiceUrl + `/internal/employersubmissions/${submissionId}/approved`;
  }

  static ipSetMemberSubmissionToReadyForApproval(submissionId: string): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/${submissionId}/readyforapproval`;
  }

  static ipSetEmployerSubmissionToReadyForApproval(submissionId: string): string {
    return environment.mainServiceUrl + `/internal/employersubmissions/${submissionId}/readyforapproval`;
  }

  static ipUpdateMemberSubmissionOwner(submissionId: string): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/${submissionId}/owner`;
  }

  static ipUpdateEmployerSubmissionOwner(submissionId: string): string {
    return environment.mainServiceUrl + `/internal/employersubmissions/${submissionId}/owner`;
  }

  static ipUpdateMemberSubmissionApprover(submissionId: string): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/${submissionId}/approver`;
  }

  static ipUpdateEmployerSubmissionApprover(submissionId: string): string {
    return environment.mainServiceUrl + `/internal/employersubmissions/${submissionId}/approver`;
  }

  static ipCreateMemberSubmissionReminder(submissionId: string): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/${submissionId}/reminders`;
  }

  static ipCreateEmployerSubmissionReminder(submissionId: string): string {
    return environment.mainServiceUrl + `/internal/employersubmissions/${submissionId}/reminders`;
  }

  static ipDeleteMemberSubmissionReminder(submissionId: string, reminderId: string): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/${submissionId}/reminders/${reminderId}`;
  }

  static ipDeleteEmployerSubmissionReminder(submissionId: string, reminderId: string): string {
    return environment.mainServiceUrl + `/internal/employersubmissions/${submissionId}/reminders/${reminderId}`;
  }

  static ipSetRelationshipStatusSubmissionToApproved(submissionId: number): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/${submissionId}/relationshipstatusapproved`;
  }

  static ipGetMemberSubmissionApprovers(): string {
    return environment.mainServiceUrl + `/odata/internal/membersubmissionapprovers`;
  }

  static ipGetEmployerSubmissionApprovers(): string {
    return environment.mainServiceUrl + `/odata/internal/employersubmissionapprovers`;
  }

  static ipGetSharedSubmissionApprovers(): string {
    return environment.mainServiceUrl + `/odata/internal/sharedsubmissionapprovers`;
  }

  static ipGetMemberSubmissionOwners(): string {
    return environment.mainServiceUrl + `/odata/internal/membersubmissionowners`;
  }

  static ipGetEmployerSubmissionOwners(): string {
    return environment.mainServiceUrl + `/odata/internal/employersubmissionowners`;
  }

  static ipGetSharedSubmissionOwners(): string {
    return environment.mainServiceUrl + `/odata/internal/sharedsubmissionowners`;
  }

  static ipGetMemberSubmissionTypes(): string {
    return environment.mainServiceUrl + `/odata/internal/membersubmissiontypes`;
  }

  static ipGetEmployerSubmissionTypes(): string {
    return environment.mainServiceUrl + `/odata/internal/employersubmissiontypes`;
  }

  static getSubmissionRoleTemplates(): string {
    return environment.mainServiceUrl + `/odata/submissionroletemplates`;
  }

  static getAllSubmissionPermissions(): string {
    return environment.mainServiceUrl + `/odata/submissionpermissions`;
  }

  static getSubmissionPermissionTypes(): string {
    return environment.mainServiceUrl + `/submissionpermissiontypes`;
  }

  static mpGetMemberSubmissionTypes(): string {
    return environment.mainServiceUrl + `/odata/member/submissiontypes`;
  }

  static mpConfirmDefaultInvestmentInstructions(): string {
    return environment.mainServiceUrl + `/member/definedcontributionaccounts/confirmdefaultinvestmentinstructions`;
  }

  static mpCreateBankingInformationSubmission(): string {
    return environment.mainServiceUrl + `/member/submissions/bankinginformation`;
  }

  static mpCreateTransferInSubmission(): string {
    return environment.mainServiceUrl + `/member/submissions/transferin`;
  }

  static ipCreateTransferInSubmission(): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/transferin`;
  }

  static ipUpdateTransferInTransferRecord(submissionId: number): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/${submissionId}/transferrecord`;
  }

  static ipCreateBankingInformationSubmission(): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/bankinginformation`;
  }

  static ipCreatePaylineAccountSubmission(): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/paylineaccount`;
  }

  static mpGetRetirementProductSubmissionAvailability(): string {
    return environment.mainServiceUrl + `/member/retirementproductsubmissionavailability`;
  }

  static ipGetRetirementProductSubmissionAvailability(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/retirementproductsubmissionavailability`;
  }

  static ipSubmitMonthlyPensionSetupSubmission(): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/monthlypensionsetup`;
  }

  // Alerts

  static createAlert(): string {
    return environment.mainServiceUrl + `/alerts`;
  }

  static updateAlert(alertId: string): string {
    return environment.mainServiceUrl + `/alerts/${alertId}`;
  }

  static getAlertById(alertId: string): string {
    return environment.mainServiceUrl + `/odata/alerts/${alertId}`;
  }

  static getAlerts(): string {
    return environment.mainServiceUrl + `/odata/alerts`;
  }

  static getActiveAlerts(): string {
    return environment.mainServiceUrl + `/odata/activealerts`;
  }

  // Transactions

  static getTransactionById(transactionId: number): string {
    return environment.mainServiceUrl + `/odata/internal/transactions/${transactionId}`;
  }

  static getTransactionsForRemittance(remittanceId: number): string {
    return environment.mainServiceUrl + `/odata/remittances/${remittanceId}/transactions`;
  }

  // Remittances

  static getRemittanceStatusTypes(): string {
    return environment.mainServiceUrl + `/remittancestatustypes`;
  }

  static getRemittancePaymentStatusTypes(): string {
    return environment.mainServiceUrl + `/remittancepaymentstatustypes`;
  }

  static getAllRemittances(): string {
    return environment.mainServiceUrl + `/odata/remittances`;
  }

  static ipGetOpenRemittancesForEmployer(employerId: number): string {
    return environment.mainServiceUrl + `/odata/internal/employers/${employerId}/openremittances`;
  }

  static getEmployerOpenRemittancesForEmployer(): string {
    return environment.mainServiceUrl + `/odata/employer/openremittances`;
  }

  static getInternalClosedRemittancesForEmployer(employerId: number): string {
    return environment.mainServiceUrl + `/odata/internal/employers/${employerId}/closedremittances`;
  }

  static getEmployerClosedRemittancesForEmployer(): string {
    return environment.mainServiceUrl + `/odata/employer/closedremittances`;
  }

  static getRemittanceById(remittanceId: number): string {
    return environment.mainServiceUrl + `/odata/remittances/${remittanceId}`;
  }

  static createRemittance(): string {
    return environment.mainServiceUrl + `/remittances`;
  }

  static submitRemittance(remittanceId: number): string {
    return environment.mainServiceUrl + `/remittances/${remittanceId}/submit`;
  }

  static addDatesToRemittance(remittanceId: number): string {
    return environment.mainServiceUrl + `/remittances/${remittanceId}/dates`;
  }

  static deleteRemittance(remittanceId: number): string {
    return environment.mainServiceUrl + `/remittances/${remittanceId}`;
  }

  static getMembersOnRemittance(employerId: number, remittanceId: number): string {
    return environment.mainServiceUrl + `/odata/employers/${employerId}/remittances/${remittanceId}/members`;
  }

  static getNewMembersOnRemittance(remittanceId: number): string {
    return environment.mainServiceUrl + `/odata/remittances/${remittanceId}/newmembers`;
  }

  static getRemittanceHistory(remittanceId: number): string {
    return environment.mainServiceUrl + `/odata/remittances/${remittanceId}/history`;
  }

  static updateRemittanceOwner(remittanceId: number): string {
    return environment.mainServiceUrl + `/remittances/${remittanceId}/updateowner`;
  }

  static updateRemittanceStatusInReview(remittanceId: number): string {
    return environment.mainServiceUrl + `/remittances/${remittanceId}/updatestatusinreview`;
  }

  static updateRemittanceStatusApproved(remittanceId: number): string {
    return environment.mainServiceUrl + `/remittances/${remittanceId}/updatestatusapproved`;
  }

  static updateRemittanceStatusFailed(remittanceId: number): string {
    return environment.mainServiceUrl + `/remittances/${remittanceId}/updatestatusfailed`;
  }

  static getRemittanceMembersToVerify(remittanceId: string): string {
    return environment.mainServiceUrl + `/odata/remittances/${remittanceId}/memberstoverify`;
  }

  static createRemittanceNewMember(remittanceId: number, contributionId: string): string {
    return environment.mainServiceUrl + `/remittances/${remittanceId}/contributions/${contributionId}/newmemberdetails`;
  }

  static updateRemittanceNewMember(remittanceId: number, contributionId: string, newMemberId: string): string {
    return (
      environment.mainServiceUrl +
      `/remittances/${remittanceId}/contributions/${contributionId}/newmemberdetails/${newMemberId}`
    );
  }

  static ipGetMemberOpenRemittancesByEmployer(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/openremittancesbyemployer`;
  }

  static ipGetVariableBenefitPaymentEstimate(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/variablebenefitpaymentestimate`;
  }

  static mpGetVariableBenefitPaymentEstimate(): string {
    return environment.mainServiceUrl + `/member/variablebenefitpaymentestimate`;
  }

  static ipGetVariableBenefitTargetPayment(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/variablebenefittargetpayment`;
  }

  static mpGetVariableBenefitTargetPayment(): string {
    return environment.mainServiceUrl + `/member/variablebenefittargetpayment`;
  }

  static ipGetMemberVariableBenefitSummary(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/variablebenefitsummary`;
  }

  static mpGetVariableBenefitSummary(): string {
    return environment.mainServiceUrl + `/member/variablebenefitsummary`;
  }

  static ipGetMemberPlanRules(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/planrules`;
  }

  static getRemittanceOwners(): string {
    return environment.mainServiceUrl + `/odata/remittanceowners`;
  }

  static getCommentsForRemittance(remittanceId: number): string {
    return environment.mainServiceUrl + `/odata/remittances/${remittanceId}/comments`;
  }

  static addRemittanceComment(remittanceId: number): string {
    return environment.mainServiceUrl + `/remittances/${remittanceId}/comments`;
  }

  static setRemittanceCommentsAsRead(): string {
    return environment.mainServiceUrl + `/remittances/comments/readreceipts`;
  }

  // Payments

  static createRemittancePayment() {
    return environment.mainServiceUrl + `/payments`;
  }

  // Contributions

  static getContributionsForRemittance(remittanceId: number): string {
    return environment.mainServiceUrl + `/odata/remittances/${remittanceId}/contributions`;
  }

  static addContributionToRemittance(remittanceId: number): string {
    return environment.mainServiceUrl + `/remittances/${remittanceId}/contributions`;
  }

  static deleteContribution(remittanceId: number, contributionId: string): string {
    return environment.mainServiceUrl + `/remittances/${remittanceId}/contributions/${contributionId}`;
  }

  static updateContribution(remittanceId: number, contributionId: string): string {
    return environment.mainServiceUrl + `/remittances/${remittanceId}/contributions/${contributionId}`;
  }

  static getHistoryForContribution(remittanceId: string, contributionId: string): string {
    return (
      environment.mainServiceUrl +
      `/odata/remittances/${remittanceId}/contributions/${contributionId}/history?$expand=OriginPortal`
    );
  }

  static verifyContribution(remittanceId: string, contributionId: string): string {
    return environment.mainServiceUrl + `/remittances/${remittanceId}/contributions/${contributionId}/verify`;
  }

  static getContributionSummaryForRemittance(remittanceId: string): string {
    return environment.mainServiceUrl + `/odata/remittances/${remittanceId}/contributionsummary`;
  }

  static getPotentialMatchesForContribution(remittanceId: string, contributionId: string): string {
    return (
      environment.mainServiceUrl +
      `/odata/remittances/${remittanceId}/contributions/${contributionId}/potentialmembermatches?$count=true`
    );
  }

  // Contribution Entries

  static createContributionEntry(remittanceId: number, contributionId: string): string {
    return environment.mainServiceUrl + `/remittances/${remittanceId}/contributions/${contributionId}/entries`;
  }

  static updateContributionEntry(remittanceId: number, contributionId: string, entryId: string): string {
    return (
      environment.mainServiceUrl + `/remittances/${remittanceId}/contributions/${contributionId}/entries/${entryId}`
    );
  }

  static deleteContributionEntry(remittanceId: number, contributionId: string, entryId: string): string {
    return (
      environment.mainServiceUrl + `/remittances/${remittanceId}/contributions/${contributionId}/entries/${entryId}`
    );
  }

  // Accounts
  static ipGetMemberAccounts(memberId: number): string {
    return environment.mainServiceUrl + `/odata/internal/members/${memberId}/accounts`;
  }

  static mpGetAccounts(): string {
    return environment.mainServiceUrl + `/odata/member/accounts`;
  }

  static ipCreateTransferredInAccount(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/transferredinaccounts`;
  }

  static getInternalMemberAccount(memberId: number, accountId: number): string {
    return environment.mainServiceUrl + `/odata/internal/members/${memberId}/accounts/${accountId}`;
  }

  static ipGetMemberAccountInvestmentInstructions(memberId: number, accountIds: number[]): string {
    return (
      environment.mainServiceUrl +
      `/internal/members/${memberId}/investmentinstructions?accountIds=${accountIds.join(',')}`
    );
  }

  static mpGetAccountInvestmentInstructions(accountIds: number[]): string {
    return environment.mainServiceUrl + `/member/investmentinstructions?accountIds=${accountIds.join(',')}`;
  }

  static getMemberAccount(accountId: number): string {
    return environment.mainServiceUrl + `/odata/member/accounts/${accountId}`;
  }

  static getMemberOnboardingProgress(): string {
    return environment.mainServiceUrl + `/odata/member/onboardingprogress`;
  }

  static ipGetMemberAccountFundDetails(memberId: number, accountId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/accounts/${accountId}/funddetails`;
  }

  static ipGetMemberVbAccountDetails(memberId: number, accountId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/variablebenefitaccounts/${accountId}/details`;
  }

  static ipGetMemberVbAccountActivityBreakdown(memberId: number, accountId: number): string {
    return (
      environment.mainServiceUrl +
      `/internal/members/${memberId}/variablebenefitaccounts/${accountId}/activitybreakdown`
    );
  }

  static ipGetMemberVbAccountNewMoneyLimits(memberId: number, accountId: number): string {
    return (
      environment.mainServiceUrl + `/internal/members/${memberId}/variablebenefitaccounts/${accountId}/newmoneylimits`
    );
  }

  static getMemberAccountFundDetails(accountId: number): string {
    return environment.mainServiceUrl + `/member/accounts/${accountId}/funddetails`;
  }

  static mpGetVbAccountDetails(accountId: number): string {
    return environment.mainServiceUrl + `/member/variablebenefitaccounts/${accountId}/details`;
  }

  static mpGetVbAccountActivityBreakdown(accountId: number): string {
    return environment.mainServiceUrl + `/member/variablebenefitaccounts/${accountId}/activitybreakdown`;
  }

  static getInternalMemberAccountContributionBalances(memberId: number, accountId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/accounts/${accountId}/contributionbalances`;
  }

  static getMemberAccountContributionBalances(accountId: number): string {
    return environment.mainServiceUrl + `/member/accounts/${accountId}/contributionbalances`;
  }

  static ipGetMemberAccountAllocationOfFunds(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/accountallocationoffunds`;
  }

  static ipGetMemberBalancesByAccountAndFund(memberId: number, accountTypeId: number): string {
    return (
      environment.mainServiceUrl +
      `/internal/members/${memberId}/balancesbyaccountandfund?accountTypeId=${accountTypeId}`
    );
  }

  static mpGetAccountAllocationOfFunds(): string {
    return environment.mainServiceUrl + `/member/accountallocationoffunds`;
  }

  static getInternalTransactionEntriesForMemberAccount(memberId: number, accountId: number): string {
    return environment.mainServiceUrl + `/odata/internal/members/${memberId}/accounts/${accountId}/transactionentries`;
  }

  static getTransactionEntriesForMemberAccount(accountId: number): string {
    return environment.mainServiceUrl + `/odata/member/accounts/${accountId}/transactionentries`;
  }

  static getInternalTransactionEntriesForTransaction(transactionId: number): string {
    return environment.mainServiceUrl + `/odata/internal/transactions/${transactionId}/entries`;
  }

  // Release Notes

  static getReleaseNoteDocuments(): string {
    return environment.mainServiceUrl + `/odata/releasenotedocuments`;
  }

  static getReleaseNoteDocumentVersions(): string {
    return environment.mainServiceUrl + `/odata/releasenotedocumentversions`;
  }

  static getReleaseNoteDocumentById(id: string): string {
    return environment.mainServiceUrl + `/odata/releasenotedocuments/${id}`;
  }

  static createReleaseNoteDocument(): string {
    return environment.mainServiceUrl + `/releasenotedocuments`;
  }

  static releaseNoteDocumentById(id: string): string {
    return environment.mainServiceUrl + `/releasenotedocuments/${id}`;
  }

  static getLatestReleaseNoteDocument(): string {
    return environment.mainServiceUrl + `/odata/latestreleasenotedocument`;
  }

  // Beneficiaries

  static ipGetMemberAccountBeneficiaries(memberId: number): string {
    return environment.mainServiceUrl + `/odata/internal/members/${memberId}/accountbeneficiaries`;
  }

  static ipGetMemberBeneficiaries(memberId: number): string {
    return environment.mainServiceUrl + `/odata/internal/members/${memberId}/beneficiaries`;
  }

  static ipGetMemberBeneficiary(memberId: number, beneficiaryId: number): string {
    return environment.mainServiceUrl + `/odata/internal/members/${memberId}/beneficiaries/${beneficiaryId}`;
  }

  static ipUpdateMemberBeneficiary(memberId: number, beneficiaryId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/beneficiaries/${beneficiaryId}`;
  }

  static mpGetAccountBeneficiaries(): string {
    return environment.mainServiceUrl + `/odata/member/accountbeneficiaries`;
  }

  static mpGetMemberBeneficiaries(): string {
    return environment.mainServiceUrl + `/odata/member/beneficiaries`;
  }

  static mpGetMemberBeneficiary(beneficiaryId: number): string {
    return environment.mainServiceUrl + `/odata/member/beneficiaries/${beneficiaryId}`;
  }

  static mpUpdateMemberBeneficiary(beneficiaryId: number): string {
    return environment.mainServiceUrl + `/member/beneficiaries/${beneficiaryId}`;
  }

  // Banking

  static mpArchiveBankAccount(bankAccountId: number): string {
    return environment.mainServiceUrl + `/member/bankaccounts/${bankAccountId}/archive`;
  }

  static ipArchiveBankAccount(memberId: number, bankAccountId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/bankaccounts/${bankAccountId}/archive`;
  }

  static ipGetBankAccountsForMember(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/bankaccounts`;
  }

  static mpGetBankAccounts(): string {
    return environment.mainServiceUrl + `/member/bankaccounts`;
  }

  static ipGetPaylineAccountsForMember(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/paylineaccounts`;
  }

  static ipArchivePaylineAccount(memberId: number, paylineAccountId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/paylineaccounts/${paylineAccountId}/archive`;
  }

  static mpGetPaylineAccountsForMember(): string {
    return environment.mainServiceUrl + `/member/paylineaccounts`;
  }

  static ipGetBankAccountUsage(memberId: number, bankAccountId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/bankaccounts/${bankAccountId}/usage`;
  }

  static mpGetBankAccountUsage(bankAccountId: number): string {
    return environment.mainServiceUrl + `/member/bankaccounts/${bankAccountId}/usage`;
  }

  static ipGetPaylineAccountUsage(memberId: number, paylineAccountId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/paylineaccounts/${paylineAccountId}/usage`;
  }

  static mpGetPaylineAccountUsage(paylineAccountId: number): string {
    return environment.mainServiceUrl + `/member/paylineaccounts/${paylineAccountId}/usage`;
  }

  // Fiduciaries

  static getFiduciaryTypes(): string {
    return environment.mainServiceUrl + `/legalfiduciarytypes`;
  }

  static getFiduciaryEntityTypes(): string {
    return environment.mainServiceUrl + `/legalfiduciaryentitytypes`;
  }

  static getFiduciaryPowerOfAttorneyTypes(): string {
    return environment.mainServiceUrl + `/legalfiduciarypowerofattorneytypes`;
  }

  static getFiduciaryPowerOfAttorneyAppointmentTypes(): string {
    return environment.mainServiceUrl + `/legalfiduciarypowerofattorneyappointmenttypes`;
  }

  static ipCreateLegalFiduciarySubmission(): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/legalfiduciary`;
  }

  // Employers

  static getEmployerTypes(): string {
    return environment.mainServiceUrl + `/employertypes`;
  }

  static getEmployerStatusTypes(): string {
    return environment.mainServiceUrl + `/employerstatustypes`;
  }

  static getMemberStatusTypes(): string {
    return environment.mainServiceUrl + `/memberstatustypes`;
  }

  static getIdentityTypes(): string {
    return environment.mainServiceUrl + `/identitytypes`;
  }

  static getGoverningJurisdictionTypes(): string {
    return environment.mainServiceUrl + `/governingjurisdictiontypes`;
  }

  static getTransferInSourceAccountTypes(): string {
    return environment.mainServiceUrl + `/transferinsourceaccounttypes`;
  }

  static getEmployerVotingRegionTypes(): string {
    return environment.mainServiceUrl + `/votingregiontypes`;
  }

  static getEmployerCRANumberInUse(craNumber: string) {
    return environment.mainServiceUrl + `/internal/employercranumberinuse?craNumber=${craNumber}`;
  }

  static ipCreateEmployer(): string {
    return environment.mainServiceUrl + `/internal/employersubmissions/newemployer`;
  }

  static epCreateEmployee(): string {
    return environment.mainServiceUrl + `/employer/member`;
  }

  static epCreateEmployees(): string {
    return environment.mainServiceUrl + `/employer/members`;
  }

  static ipUpdateEmployerAddress(): string {
    return environment.mainServiceUrl + `/internal/employersubmissions/updateaddress`;
  }

  static ipUpdateEmployerContacts(): string {
    return environment.mainServiceUrl + `/internal/employersubmissions/updatecontacts`;
  }

  static ipCreateEmploymentStatusChangeSubmission(): string {
    return environment.mainServiceUrl + `/internal/employersubmissions/updateemploymentstatus`;
  }

  static epUpdateEmployerContacts(): string {
    return environment.mainServiceUrl + `/employer/submissions/updatecontacts`;
  }

  // Variable Benefit

  static getVbMinMaxWithdrawalAmounts(): string {
    return environment.mainServiceUrl + `/variablebenefitminmaxwithdrawalamounts`;
  }

  static ipGetLockedUnlockedBalances(memberId: number, overrideGoverningJurisdictionId: number | null): string {
    let url = environment.mainServiceUrl + `/internal/members/${memberId}/lockedunlockedbalances`;
    if (overrideGoverningJurisdictionId !== null) {
      url += `?overrideGoverningJurisdictionId=${overrideGoverningJurisdictionId}`;
    }

    return url;
  }

  static ipCalculateJurisdictionCommutableThresholds(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/jurisdictioncommutablethresholds`;
  }

  static ipGetMemberDefinedContributionGoverningJurisdiction(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/definedcontributiongoverningjurisdiction`;
  }

  static ipGetMemberVariableBenefitWithdrawalInstructions(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/variablebenefitwithdrawalinstructions`;
  }

  static ipGetMemberUpcomingVbPayments(memberId: number, paymentCount: number): string {
    let url = environment.mainServiceUrl + `/internal/members/${memberId}/variablebenefitupcomingpayments`;
    if (paymentCount > 0) {
      url += `?paymentCount=${paymentCount}`;
    }

    return url;
  }

  static ipGetMemberActiveFundingVbSubmission(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/activefundingvariablebenefitsubmission`;
  }

  static mpGetActiveFundingVbSubmission(): string {
    return environment.mainServiceUrl + '/member/activefundingvariablebenefitsubmission';
  }

  static mpGetUpcomingVbPayments(paymentCount: number): string {
    let url = environment.mainServiceUrl + '/member/variablebenefitupcomingpayments';
    if (paymentCount > 0) {
      url += `?paymentCount=${paymentCount}`;
    }

    return url;
  }

  static mpGetVariableBenefitWithdrawalInstructions(excludeZeroPercentFunds: boolean): string {
    return (
      environment.mainServiceUrl +
      `/member/variablebenefitwithdrawalinstructions?excludeZeroPercentFunds=${excludeZeroPercentFunds}`
    );
  }

  static ipGetMemberActiveVariableBenefitConfigSubmission(memberId: number) {
    return (
      environment.mainServiceUrl + `/odata/internal/members/${memberId}/activevariablebenefitconfigurationsubmission`
    );
  }

  static ipSaveVariableBenefitConfigurationSubmission(): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/variablebenefitconfiguration/save`;
  }

  static ipSubmitVariableBenefitConfigurationSubmission(): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/variablebenefitconfiguration/submit`;
  }

  static ipDeleteVariableBenefitSubmission(submissionId: number): string {
    return (
      environment.mainServiceUrl + `/internal/membersubmissions/variablebenefitconfiguration/${submissionId}/delete`
    );
  }

  static ipSubmitUpdateVariableBenefitConfigurationSubmission(): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/updatevariablebenefitconfiguration`;
  }

  static ipSubmitVariableBenefitPaymentChangeSubmission(): string {
    return environment.mainServiceUrl + `/internal/membersubmissions/variablebenefitpaymentchange`;
  }

  static mpSubmitVariableBenefitPaymentChangeSubmission(): string {
    return environment.mainServiceUrl + `/member/submissions/variablebenefitpaymentchange`;
  }

  // Spousal Waivers

  static ipGetSpousalWaiversForMember(memberId: number): string {
    return environment.mainServiceUrl + `/odata/internal/members/${memberId}/spousalwaivers`;
  }

  static ipCreateSpousalWaiver(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/spousalwaivers`;
  }

  static ipGetMemberSpousalWaiverById(memberId: number, spousalWaiverId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/spousalwaivers/${spousalWaiverId}`;
  }

  static getSpousalWaiverTypes(): string {
    return environment.mainServiceUrl + `/memberspousalwaivertypes`;
  }

  static ipUpdateMemberSpousalWaiver(memberId: number, spousalWaiverId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/spousalwaivers/${spousalWaiverId}`;
  }

  static ipDeleteVariableBenefitSpousalWaiver(submissionId: number, waiverId: string): string {
    return (
      environment.mainServiceUrl +
      `/internal/membersubmissions/variablebenefitconfiguration/${submissionId}/spousalwaivers/${waiverId}`
    );
  }

  // Monthly Pensions

  static getAnnuityGuaranteePeriodTypes(): string {
    return environment.mainServiceUrl + `/annuityguaranteeperiodtypes`;
  }

  static ipCreateSingleLifeAnnuityCalculation(): string {
    return environment.mainServiceUrl + `/annuitysinglelifecalculations`;
  }

  static ipCreateJointLifeAnnuityCalculation(): string {
    return environment.mainServiceUrl + `/annuityjointlifecalculations`;
  }

  static ipGetAnnuityOperationalConversionRateForDate(date: string): string {
    return environment.mainServiceUrl + `/internal/annuityoperationalconversionratefordate?date=${date}`;
  }

  static getAnnuityTypes(): string {
    return environment.mainServiceUrl + `/annuitytypes`;
  }

  static getDeathRecordContactMethodTypes(): string {
    return environment.mainServiceUrl + `/deathrecordcontactmethodtypes`;
  }

  static getDeathRecordSettlementTypes(): string {
    return environment.mainServiceUrl + `/deathrecordsettlementtypes`;
  }

  static ipGetMemberMonthlyPensionAccountDetails(memberId: number, accountId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/monthlypensionaccounts/${accountId}/details`;
  }

  static mpGetMonthlyPensionAccountDetails(accountId: number): string {
    return environment.mainServiceUrl + `/member/monthlypensionaccounts/${accountId}/details`;
  }

  static ipGetMemberMonthlyPensionEntitlements(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/monthlypensionentitlements`;
  }

  static mpGetMonthlyPensionEntitlements(): string {
    return environment.mainServiceUrl + `/member/monthlypensionentitlements`;
  }

  static ipUpdateMonthlyPensionPaymentInstructionDepositInstructions(
    memberId: number,
    accountId: number,
    paymentInstructionId: string
  ): string {
    return (
      environment.mainServiceUrl +
      `/internal/members/${memberId}/monthlypensionaccounts/${accountId}/paymentinstructions/${paymentInstructionId}/depositinstructions`
    );
  }

  static mpUpdateMonthlyPensionPaymentInstructionDepositInstructions(
    accountId: number,
    paymentInstructionId: string
  ): string {
    return (
      environment.mainServiceUrl +
      `/member/monthlypensionaccounts/${accountId}/paymentinstructions/${paymentInstructionId}/depositinstructions`
    );
  }

  static ipTerminateMonthlyPensionPaymentInstruction(
    memberId: number,
    accountId: number,
    paymentInstructionId: string
  ): string {
    return (
      environment.mainServiceUrl +
      `/internal/members/${memberId}/monthlypensionaccounts/${accountId}/paymentinstructions/${paymentInstructionId}/terminate`
    );
  }

  static ipEngageMonthlyPensionJointLifeSurvivorship(memberId: number, accountId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/monthlypensionaccounts/${accountId}/engagejls`;
  }

  static ipSplitMonthlyPension(memberId: number, accountId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/monthlypensionaccounts/${accountId}/split`;
  }

  // Taxes

  static ipGetTaxForms(): string {
    return environment.mainServiceUrl + `/odata/internal/taxforms`;
  }

  static ipGetPrescribedTaxZoneConfigurations(): string {
    return environment.mainServiceUrl + `/odata/internal/prescribedtaxzoneconfigurations`;
  }

  static ipGetNonResidentTaxWithholdingTreatyRates(): string {
    return environment.mainServiceUrl + `/odata/internal/nonresidenttaxwithholdingtreatyrates`;
  }

  static ipGetTaxForm(taxFormId: number): string {
    return environment.mainServiceUrl + `/odata/internal/taxforms/${taxFormId}`;
  }

  static ipGetTaxFormFields(taxFormId: number): string {
    return environment.mainServiceUrl + `/odata/internal/taxforms/${taxFormId}/formfields`;
  }

  static ipGetMemberTaxProfiles(memberId: number): string {
    return environment.mainServiceUrl + `/odata/internal/members/${memberId}/taxprofiles`;
  }

  static ipGetMemberTaxProfile(memberId: number, memberTaxProfileId: number): string {
    return environment.mainServiceUrl + `/odata/internal/members/${memberId}/taxprofiles/${memberTaxProfileId}`;
  }

  static ipCreateMemberTaxProfile(memberId: number): string {
    return environment.mainServiceUrl + `/internal/members/${memberId}/taxprofiles`;
  }

  static ipCreateMemberTaxProfileCraLetterReference(memberId: number, memberTaxProfileId: number): string {
    return (
      environment.mainServiceUrl + `/internal/members/${memberId}/taxprofiles/${memberTaxProfileId}/craletterreferences`
    );
  }

  static ipCreateMemberTaxProfileCraLetterReferenceAttachment(memberId: number, memberTaxProfileId: number): string {
    return (
      environment.mainServiceUrl +
      `/internal/members/${memberId}/taxprofiles/${memberTaxProfileId}/craletterreferenceattachments`
    );
  }

  static ipCreateMemberTaxProfileSupportingDocumentationAttachment(
    memberId: number,
    memberTaxProfileId: number
  ): string {
    return (
      environment.mainServiceUrl +
      `/internal/members/${memberId}/taxprofiles/${memberTaxProfileId}/supportingdocumentationattachments`
    );
  }
}
